import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useWishlist } from "../hooks/useWishlist";
import HotelCard from "../components/HotelCard";
import { Heart, ArrowRight } from "@phosphor-icons/react";

export default function WishlistPage() {
  const { refresh } = useWishlist();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const d = await refresh();
      if (d) setData(d);
      else toast.error("Please log in to see your wishlist");
    })();
  }, [refresh]);

  if (!data) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const empty = data.hotels.length === 0 && data.properties.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="wishlist-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Saved for later</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-10 flex items-center gap-3">
        <Heart size={36} weight="fill" className="text-red-500" /> My Wishlist
      </h1>

      {empty && (
        <div className="text-center py-24 bg-white rounded-2xl border border-black/5" data-testid="wishlist-empty">
          <p className="font-serif text-2xl text-midnight mb-3">No favourites yet</p>
          <p className="text-neutral-500 mb-6">Tap the heart on any hotel or rental to save it here.</p>
          <Link to="/hotels" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-midnight text-bone hover:bg-midnight-hover transition-colors">
            Browse hotels <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {data.hotels.length > 0 && (
        <>
          <h2 className="font-serif text-2xl text-midnight mb-4">Hotels ({data.hotels.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {data.hotels.map((h) => <HotelCard key={h.id} hotel={h} />)}
          </div>
        </>
      )}

      {data.properties.length > 0 && (
        <>
          <h2 className="font-serif text-2xl text-midnight mb-4">Rental Rooms ({data.properties.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.properties.map((p) => (
              <Link key={p.id} to={`/rentals/${p.id}`} className="block bg-white rounded-2xl border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition">
                <div className="aspect-[4/3] bg-stone overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-lg text-midnight mb-1">{p.title}</h3>
                  <p className="text-sm text-neutral-500 mb-3">{p.city} · {p.room_type}</p>
                  <p className="font-serif text-xl text-midnight">₹{(p.monthly_rent || 0).toLocaleString("en-IN")}<span className="text-xs text-neutral-500"> / month</span></p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
